<template>
  <drop-down
    :menus="editorStore.attrDisabled ? [] : shapeMenus"
    :trigger="'click'"
  >
    <div
      class="flex justify-center items-center flex-col hover:opacity-50 w-20"
      :class="{
        'opacity-50': editorStore.attrDisabled
      }"
    >
      <Icon :icon="'add-element.svg'" :width="20" :height="20" fill="none" />
      <span class="text-[12px] mt-[5px] opacity-70">基本元素</span>
    </div>
  </drop-down>

  <modal-info-progress-bar
    ref="progressBarModal"
    @add-progress-bar="addProgressBar"
  />
</template>

<script lang="ts" setup>
import {
  ICON_SRC,
  useGlobalShortcuts,
  useShapeManage,
  useShapeSprite,
  useWatchfaceDevices
} from '@/hooks';
import { ElectronApi } from '@/services/electron.api';
import { ShapeType } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { useLayerStore } from '@/store/use-layer-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUse } from '@/utils/can-use.utils';
import { getTextConfig } from '@/utils/shape/text.utils';
import { Modal } from 'ant-design-vue';
import { computed, ref } from 'vue';

const { addShape } = useShapeManage();
const { pickThenUploadSpriteImages } = useShapeSprite();
const { findDeivce } = useWatchfaceDevices();

const addImageArray = () => {
  addShape(ShapeType.ImageArray, {});
};

const editorStore = useEditorStore();
const layerStore = useLayerStore();
const watchStore = useWatchfaceStore();
const fileName = computed(() =>
  ['O61', 'Q61'].includes(watchStore.deviceType) ? 'APK' : 'RPK'
);
const firstClickProgressBar = ref(true);
const progressBarModal = ref();

const addProgressBar = () => {
  addShape(ShapeType.ProgressBar, {
    uploadImageBefore: 'progress'
  });
};
const shapeMenus = computed(() => {
  const disabled = (editorStore.themeIndex ?? 0) > 0;
  const isAOD = editorStore.theme?.type === 'AOD';
  const res: DropDownMenuItem[] = [
    {
      icon: ICON_SRC[ShapeType.Text],
      title: '文本',
      shortcuts: ['T'],
      disabled,
      hidden: canUse('shape-text').valid ? false : true,
      onClick: async () => {
        if (!canUse('shape-text').valid) return;
        const { width, height, fontId, fontWeight } = await getTextConfig();
        addShape(ShapeType.Text, {
          attrs: {
            fontId,
            fontWeight,
            w: width ?? 100,
            h: height ?? 50
          }
        });
      }
    },
    {
      icon: ICON_SRC[ShapeType.Image],
      title: '图片',
      shortcuts: ['Shift', 'CommandOrControl', 'K'],
      disabled,

      onClick: () => {
        const deviceInfo = findDeivce(watchStore.deviceType);
        addShape(ShapeType.Image, {
          uploadImageBefore: 'pointer0',
          attrs: {
            pointerFps: deviceInfo?.defaultPointerFps ?? 1
          }
        });
      }
    },
    {
      icon: ICON_SRC[ShapeType.ImageArray],
      title: '图组',
      shortcuts: ['Shift', 'K'],
      disabled,

      onClick: () => {
        setTimeout(() => {
          addImageArray();
        }, 0);
      }
    },
    {
      icon: ICON_SRC[ShapeType.ProgressBar],
      shortcuts: ['P'],
      title: '进度条',
      disabled,
      hidden: !canUse('shape-progress-bar').valid,
      onClick: () => {
        if (firstClickProgressBar.value) {
          progressBarModal.value.showModal();
          firstClickProgressBar.value = false;
        } else {
          addProgressBar();
        }
      }
    },

    {
      delimiter: '特殊',
      hidden: isAOD && !canUse('aod-shape-app-widget').valid
    },
    {
      icon: ICON_SRC[ShapeType.Sprite],
      title: canUse('shape-video').valid ? '动画序列帧/视频' : '动画序列帧',
      disabled: disabled,
      hidden: isAOD || !canUse('shape-sprite').valid,
      onClick: async () => {
        const shape = await addShape(ShapeType.Sprite, {});
        // 选择序列帧图片
        if (shape && !canUse('shape-video').valid) {
          const res = await pickThenUploadSpriteImages(shape);
          if (!res) {
            shape.destroy();
          } else {
            // 如果序列帧是全屏的，则将x y坐标设置为0,0
            const { width = 0, height = 0 } =
              shape.getImageSize('images', editorStore.themeColorId ?? '') ??
              {};
            const { width: w0, height: h0 } = watchStore.size;
            if (width >= w0 && height >= h0)
              shape.setAttrs({
                x: 0,
                y: 0,
                spriteFps: 20
              });
          }
        }
      }
    },
    {
      icon: ICON_SRC[ShapeType.AppWidget],
      title: '复杂功能',
      hidden:
        ElectronApi.isNormal() || !canUse('shape-app-widget').valid || (isAOD && !canUse('aod-shape-app-widget').valid),
      onClick: () => {
        addShape(ShapeType.AppWidget, {});
      }
    },
    {
      icon: ICON_SRC[ShapeType.JsFile],
      title: 'JS/LUA程序',
      hidden: ElectronApi.isNormal() || isAOD || !canUse('shape-js-lua').valid,
      onClick: () => {
        addShape(ShapeType.JsFile, {
          attrs: {
            w: 100,
            h: 100
          }
        });
      }
    },
    // rive
    {
      icon: ICON_SRC[ShapeType.Rive],
      title: 'Rive动效',
      hidden:
        // 不可使用的情况如下
        isAOD || !canUse('rive').valid ? true : false,
      onClick: () => {
        // 判断是否已存在一个Rive动效
        const exists = editorStore.theme?.children.find(
          (item) => item.type === ShapeType.Rive
        );
        if (exists) {
          Modal.warning({
            title: '无法添加',
            content: '同一个样式下仅支持一个Rive动效，不可再次添加'
          });
          return;
        }
        // 判断是否为普通表盘
        if (watchStore.watchfaceType != 'normal') {
          Modal.warning({
            title: '无法添加',
            content: '仅能在普通表盘中添加Rive动效'
          });
          return;
        }
        addShape(ShapeType['Rive'], {});
      }
    },
    {
      icon: ICON_SRC[ShapeType.ApkFile],
      title: fileName.value + '程序',
      hidden: ElectronApi.isNormal() || isAOD || !canUse('shape-apk').valid,
      onClick: () => {
        const exists = (layerStore.themes ?? []).some((theme) =>
          (theme.children ?? []).some((item) => item.type === ShapeType.ApkFile)
        );
        if (exists) {
          Modal.warning({
            title: '无法添加',
            content:
              '一个表盘下仅支持一个' +
              fileName.value +
              '程序' +
              '，不可再次添加'
          });
          return;
        }
        addShape(ShapeType.ApkFile, {
          attrs: {
            w: 100,
            h: 100
          }
        });
      }
    }
  ];
  // 如果最后是特殊，则去掉
  const delimiterIndex = res.findIndex((item) => item.delimiter);
  // 如果分割线后面，全是隐藏元素，则将分割线隐藏
  if (
    res.every((item, index) => (index > delimiterIndex ? !!item.hidden : true))
  ) {
    res[delimiterIndex].hidden = true;
  }

  return res;
});

// 新增元素快捷键
useGlobalShortcuts(
  shapeMenus.value
    .filter((item) => item.shortcuts?.length)
    .map((item) => {
      return {
        keyboardKeys: [...(item.shortcuts ?? [])],
        handler: () => {
          const disabled = (editorStore.themeIndex ?? 0) > 0;
          if (disabled) return;
          item.onClick?.();
        }
      };
    })
);
</script>
